import React, { useMemo, useState } from 'react';
import {
  Archive,
  Award,
  BriefcaseBusiness,
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  FileText,
  Filter,
  GitBranch,
  Search,
  ShieldCheck,
  UserSquare,
  Users,
} from 'lucide-react';
import '../Employees/Employees.css';

const statusClass = (value) => String(value || 'Active').toLowerCase().replace(/\s+/g, '-');

const PAGE_CONFIGS = {
  grades: {
    icon: Award,
    title: 'Grades',
    description: 'Kelola level jabatan, rentang grade, dan mapping struktur karier karyawan.',
    sourceLabel: 'Mock data',
    tableTitle: 'Grade List',
    searchPlaceholder: 'Search grade, level, category...',
    summary: [
      { label: 'Total grades', icon: Award, tone: 'teal', valueKey: 'total' },
      { label: 'Active grades', icon: ShieldCheck, tone: 'blue', valueKey: 'active' },
      { label: 'Career bands', icon: GitBranch, tone: 'purple', valueKey: 'bands' },
      { label: 'Mapped employees', icon: Users, tone: 'orange', valueKey: 'employees' },
    ],
    columns: [
      { key: 'name', label: 'Grade', primary: true },
      { key: 'band', label: 'Band' },
      { key: 'level', label: 'Level' },
      { key: 'employees', label: 'Employees', type: 'count' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Description' },
    ],
    rows: [
      { id: 'GRD-01', name: 'Junior Staff', band: 'Operational', level: 'L1', employees: 12, status: 'Active', description: 'Entry role untuk karyawan baru dan junior.' },
      { id: 'GRD-02', name: 'Officer', band: 'Professional', level: 'L2', employees: 21, status: 'Active', description: 'Role pelaksana mandiri dengan supervisi terbatas.' },
      { id: 'GRD-03', name: 'Senior Officer', band: 'Professional', level: 'L3', employees: 16, status: 'Active', description: 'Role pelaksana senior dan mentor teknis.' },
      { id: 'GRD-04', name: 'Manager', band: 'Management', level: 'M1', employees: 7, status: 'Active', description: 'Role pengelola fungsi, target, dan tim.' },
      { id: 'GRD-05', name: 'Director', band: 'Leadership', level: 'D1', employees: 2, status: 'Active', description: 'Role strategic leadership dan decision maker.' },
    ],
  },
  'organization-structure': {
    icon: GitBranch,
    title: 'Organization Structure',
    description: 'Visualisasi struktur organisasi, reporting line, dan owner unit kerja.',
    sourceLabel: 'Mock data',
    tableTitle: 'Organization Units',
    searchPlaceholder: 'Search unit, parent, manager...',
    summary: [
      { label: 'Organization units', icon: Building2, tone: 'teal', valueKey: 'total' },
      { label: 'Departments', icon: BriefcaseBusiness, tone: 'blue', valueKey: 'departments' },
      { label: 'Managers', icon: UserSquare, tone: 'purple', valueKey: 'managers' },
      { label: 'Employees mapped', icon: Users, tone: 'orange', valueKey: 'employees' },
    ],
    columns: [
      { key: 'name', label: 'Organization unit', primary: true },
      { key: 'parent', label: 'Parent' },
      { key: 'manager', label: 'Manager' },
      { key: 'employees', label: 'Employees', type: 'count' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Scope' },
    ],
    rows: [
      { id: 'ORG-01', name: 'Business Assurance', parent: 'CBQA Global', manager: 'Helda Mutiara', employees: 18, status: 'Active', description: 'Unit delivery audit dan certification assurance.' },
      { id: 'ORG-02', name: 'IT Audit', parent: 'Business Assurance', manager: 'Edwin Chaidir', employees: 8, status: 'Active', description: 'Unit audit IT, cybersecurity, dan compliance.' },
      { id: 'ORG-03', name: 'Sustainability', parent: 'Business Assurance', manager: 'Nabila Pratiwi', employees: 9, status: 'Active', description: 'Unit layanan sustainability dan ESG assurance.' },
      { id: 'ORG-04', name: 'Operations', parent: 'CBQA Global', manager: 'Fauzi Rizalludin', employees: 11, status: 'Active', description: 'Unit operasional project, scheduling, dan resource.' },
    ],
  },
  competencies: {
    icon: Award,
    title: 'Competencies',
    description: 'Daftar kompetensi karyawan untuk kebutuhan penugasan, audit, dan pengembangan.',
    sourceLabel: 'Mock data',
    tableTitle: 'Competency Matrix',
    searchPlaceholder: 'Search competency, category, owner...',
    summary: [
      { label: 'Competencies', icon: Award, tone: 'teal', valueKey: 'total' },
      { label: 'Certified users', icon: ShieldCheck, tone: 'blue', valueKey: 'certified' },
      { label: 'Skill categories', icon: GitBranch, tone: 'purple', valueKey: 'categories' },
      { label: 'Need renewal', icon: Clock3, tone: 'orange', valueKey: 'renewal' },
    ],
    columns: [
      { key: 'name', label: 'Competency', primary: true },
      { key: 'category', label: 'Category' },
      { key: 'owner', label: 'Owner' },
      { key: 'employees', label: 'Employees', type: 'count' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Description' },
    ],
    rows: [
      { id: 'CMP-01', name: 'ISO/IEC 27001', category: 'Information Security', owner: 'IT Audit', employees: 6, status: 'Active', description: 'Kompetensi audit ISMS dan kontrol keamanan informasi.' },
      { id: 'CMP-02', name: 'Risk Assessment', category: 'Audit Methodology', owner: 'Business Assurance', employees: 11, status: 'Active', description: 'Kompetensi identifikasi dan evaluasi risiko.' },
      { id: 'CMP-03', name: 'COBIT', category: 'IT Governance', owner: 'IT Audit', employees: 4, status: 'Active', description: 'Kompetensi tata kelola TI dan control objective.' },
      { id: 'CMP-04', name: 'ESG Reporting', category: 'Sustainability', owner: 'Sustainability', employees: 5, status: 'Active', description: 'Kompetensi penyusunan dan assurance laporan ESG.' },
    ],
  },
  'employee-assignments': {
    icon: UserSquare,
    title: 'Employee Assignments',
    description: 'Monitoring penugasan karyawan aktif, project terkait, dan kesiapan resource.',
    sourceLabel: 'Mock data',
    tableTitle: 'Assignment List',
    searchPlaceholder: 'Search employee, project, role...',
    summary: [
      { label: 'Assignments', icon: UserSquare, tone: 'teal', valueKey: 'total' },
      { label: 'Active assignments', icon: BriefcaseBusiness, tone: 'blue', valueKey: 'active' },
      { label: 'Departments involved', icon: Building2, tone: 'purple', valueKey: 'departments' },
      { label: 'Available soon', icon: Clock3, tone: 'orange', valueKey: 'availableSoon' },
    ],
    columns: [
      { key: 'name', label: 'Employee', primary: true },
      { key: 'role', label: 'Role' },
      { key: 'project', label: 'Project' },
      { key: 'period', label: 'Period' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Notes' },
    ],
    rows: [
      { id: 'ASN-01', name: 'Karunia Nurhayati', role: 'Senior IT Auditor', project: 'GAP Assessment, Pentes PJP, PCI-DSS', period: 'Jul 2026', status: 'Active', description: 'Assigned sebagai auditor utama untuk workstream desain kontrol.' },
      { id: 'ASN-02', name: 'Fauzi Rizalludin', role: 'Project Coordinator', project: 'Jasa Assessment SPBE', period: 'Jul-Aug 2026', status: 'Active', description: 'Koordinasi schedule, task, dan komunikasi operasional.' },
      { id: 'ASN-03', name: 'Elsha Widya', role: 'Auditor', project: 'IT Audit Compliance', period: 'Aug 2026', status: 'Planned', description: 'Menunggu kickoff dan konfirmasi dokumen awal.' },
      { id: 'ASN-04', name: 'Lintang Ayu', role: 'Business Analyst', project: 'Audit Latik SPBE', period: 'Sep 2026', status: 'Planned', description: 'Mapping kebutuhan dan proses bisnis client.' },
    ],
  },
  'employment-administration': {
    icon: FileText,
    title: 'Employment Administration',
    description: 'Administrasi status kerja, masa kerja, kontrak, dan perubahan data karyawan.',
    sourceLabel: 'Mock data',
    tableTitle: 'Administration Records',
    searchPlaceholder: 'Search employee, document, status...',
    summary: [
      { label: 'Records', icon: FileText, tone: 'teal', valueKey: 'total' },
      { label: 'Active employees', icon: Users, tone: 'blue', valueKey: 'active' },
      { label: 'Pending updates', icon: Clock3, tone: 'purple', valueKey: 'pending' },
      { label: 'Contract review', icon: Archive, tone: 'orange', valueKey: 'review' },
    ],
    columns: [
      { key: 'name', label: 'Employee', primary: true },
      { key: 'employmentType', label: 'Employment type' },
      { key: 'joinDate', label: 'Join date' },
      { key: 'owner', label: 'HR owner' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Notes' },
    ],
    rows: [
      { id: 'ADM-01', name: 'Nabila Pratiwi', employmentType: 'Permanent', joinDate: '25 Jun 2026', owner: 'HR Operations', status: 'Active', description: 'Data utama lengkap dan aktif.' },
      { id: 'ADM-02', name: 'Diki Firmansyah', employmentType: 'Permanent', joinDate: '10 Jan 2025', owner: 'HR Operations', status: 'Active', description: 'Menunggu update emergency contact.' },
      { id: 'ADM-03', name: 'Ardiyan Fernando', employmentType: 'Contract', joinDate: '15 Mar 2025', owner: 'HR Operations', status: 'Review', description: 'Kontrak perlu review sebelum perpanjangan.' },
      { id: 'ADM-04', name: 'Helda Mutiara', employmentType: 'Permanent', joinDate: '01 Feb 2024', owner: 'HR Manager', status: 'Active', description: 'Data employment sudah tervalidasi.' },
    ],
  },
  'employee-documents': {
    icon: Archive,
    title: 'Employee Documents',
    description: 'Register dokumen karyawan seperti kontrak, sertifikat, identitas, dan dokumen HR.',
    sourceLabel: 'Mock data',
    tableTitle: 'Document Register',
    searchPlaceholder: 'Search employee, document, category...',
    summary: [
      { label: 'Documents', icon: Archive, tone: 'teal', valueKey: 'total' },
      { label: 'Approved', icon: ShieldCheck, tone: 'blue', valueKey: 'approved' },
      { label: 'Need review', icon: Clock3, tone: 'purple', valueKey: 'review' },
      { label: 'Expired soon', icon: FileText, tone: 'orange', valueKey: 'expired' },
    ],
    columns: [
      { key: 'name', label: 'Document', primary: true },
      { key: 'employee', label: 'Employee' },
      { key: 'category', label: 'Category' },
      { key: 'version', label: 'Version' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Validity' },
    ],
    rows: [
      { id: 'DOC-01', name: 'Employment Contract', employee: 'Ardiyan Fernando', category: 'Contract', version: 'v2', status: 'Review', description: 'Valid until 31 Dec 2026.' },
      { id: 'DOC-02', name: 'ISO 27001 Certificate', employee: 'Karunia Nurhayati', category: 'Certificate', version: 'v1', status: 'Approved', description: 'Valid until 20 Nov 2027.' },
      { id: 'DOC-03', name: 'Employee ID', employee: 'Nabila Pratiwi', category: 'Identity', version: 'v1', status: 'Approved', description: 'No expiry date.' },
      { id: 'DOC-04', name: 'Training Record', employee: 'Fauzi Rizalludin', category: 'Learning', version: 'v3', status: 'Submitted', description: 'Pending HR review.' },
    ],
  },
  'employee-reports': {
    icon: FileText,
    title: 'Employee Reports',
    description: 'Ringkasan laporan karyawan untuk kebutuhan monitoring, audit, dan manajemen.',
    sourceLabel: 'Mock data',
    tableTitle: 'Report Library',
    searchPlaceholder: 'Search report, owner, period...',
    summary: [
      { label: 'Reports', icon: FileText, tone: 'teal', valueKey: 'total' },
      { label: 'Monthly reports', icon: Clock3, tone: 'blue', valueKey: 'monthly' },
      { label: 'Ready to export', icon: Archive, tone: 'purple', valueKey: 'ready' },
      { label: 'Dashboards', icon: Building2, tone: 'orange', valueKey: 'dashboards' },
    ],
    columns: [
      { key: 'name', label: 'Report', primary: true },
      { key: 'period', label: 'Period' },
      { key: 'owner', label: 'Owner' },
      { key: 'frequency', label: 'Frequency' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Purpose' },
    ],
    rows: [
      { id: 'RPT-01', name: 'Employee Headcount', period: 'Jul 2026', owner: 'HR Manager', frequency: 'Monthly', status: 'Ready', description: 'Monitoring jumlah karyawan per department dan branch.' },
      { id: 'RPT-02', name: 'Competency Coverage', period: 'Q3 2026', owner: 'HR Development', frequency: 'Quarterly', status: 'Ready', description: 'Melihat coverage kompetensi dan kebutuhan renewal.' },
      { id: 'RPT-03', name: 'Assignment Load', period: 'Jul 2026', owner: 'Operations', frequency: 'Weekly', status: 'Draft', description: 'Monitoring beban penugasan karyawan.' },
      { id: 'RPT-04', name: 'Document Compliance', period: 'Jul 2026', owner: 'HR Operations', frequency: 'Monthly', status: 'Ready', description: 'Monitoring kelengkapan dokumen karyawan.' },
    ],
  },
};

const uniqueCount = (rows, key) => new Set(rows.map((row) => row[key]).filter(Boolean)).size;

const resolveSummaryValue = (rows, key) => {
  if (key === 'total') return rows.length;
  if (key === 'active') return rows.filter((row) => row.status === 'Active').length;
  if (key === 'bands') return uniqueCount(rows, 'band');
  if (key === 'employees') return rows.reduce((sum, row) => sum + Number(row.employees || 0), 0);
  if (key === 'departments') return uniqueCount(rows, 'department') || uniqueCount(rows, 'parent');
  if (key === 'managers') return uniqueCount(rows, 'manager');
  if (key === 'certified') return rows.reduce((sum, row) => sum + Number(row.employees || 0), 0);
  if (key === 'categories') return uniqueCount(rows, 'category');
  if (key === 'renewal') return rows.filter((row) => ['Review', 'Submitted'].includes(row.status)).length;
  if (key === 'availableSoon') return rows.filter((row) => row.status === 'Planned').length;
  if (key === 'pending') return rows.filter((row) => ['Review', 'Submitted', 'Draft'].includes(row.status)).length;
  if (key === 'review') return rows.filter((row) => row.status === 'Review').length;
  if (key === 'approved') return rows.filter((row) => ['Approved', 'Ready'].includes(row.status)).length;
  if (key === 'expired') return rows.filter((row) => String(row.description || '').toLowerCase().includes('valid until')).length;
  if (key === 'monthly') return rows.filter((row) => row.frequency === 'Monthly').length;
  if (key === 'ready') return rows.filter((row) => row.status === 'Ready').length;
  if (key === 'dashboards') return rows.filter((row) => String(row.name || '').toLowerCase().includes('headcount')).length;
  return 0;
};

function Status({ value }) {
  return <span className={`employee-status ${statusClass(value)}`}><i />{value}</span>;
}

function Cell({ row, column }) {
  const value = row[column.key] || '—';
  if (column.primary) {
    return (
      <div className="name-cell">
        <span className="department-avatar"><column.icon size={17} /></span>
        <span><strong>{value}</strong><small>{row.id}</small></span>
      </div>
    );
  }
  if (column.type === 'status') return <Status value={value} />;
  if (column.type === 'count') return <span className="assignment-count"><Users size={15} /> {value}</span>;
  return value;
}

const DummyEmployeeManagementPage = ({ pageKey }) => {
  const config = PAGE_CONFIGS[pageKey] || PAGE_CONFIGS.grades;
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('All statuses');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const Icon = config.icon;
  const rows = config.rows.map((row) => ({
    ...row,
    status: row.status || 'Active',
  }));

  const statuses = [...new Set(rows.map((row) => row.status))];
  const filtered = useMemo(() => rows.filter((row) => {
    const haystack = Object.values(row).join(' ').toLowerCase();
    return (!query || haystack.includes(query.toLowerCase()))
      && (status === 'All statuses' || row.status === status);
  }), [query, rows, status]);

  const visible = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const activeFilters = status !== 'All statuses' ? 1 : 0;

  const reset = () => {
    setQuery('');
    setStatus('All statuses');
    setPage(1);
  };

  return (
    <div className="employee-directory-page hris-master-page">
      <header className="employee-page-header">
        <div>
          <div className="eyebrow"><Icon size={15} /> HUMAN RESOURCES</div>
          <h1>{config.title}</h1>
          <p>{config.description}</p>
        </div>
        <div className="source-state">
          <span><i /> {config.sourceLabel}</span>
          <small>Belum tersinkron ke backend HRIS.</small>
        </div>
      </header>

      <section className="employee-summary-grid">
        {config.summary.map((item) => {
          const SummaryIcon = item.icon;
          return (
            <article key={item.label}>
              <span className={`summary-icon ${item.tone}`}><SummaryIcon size={21} /></span>
              <div><small>{item.label}</small><strong>{resolveSummaryValue(rows, item.valueKey)}</strong></div>
            </article>
          );
        })}
      </section>

      <section className="employee-filter-panel">
        <div className="filter-panel-head">
          <div>
            <h2>{config.tableTitle}</h2>
            <p>Gunakan pencarian dan filter untuk menemukan data yang sesuai.</p>
          </div>
          <button className="filter-toggle" type="button">
            <Filter size={16} /> Filters {activeFilters > 0 && <b>{activeFilters}</b>}
          </button>
        </div>
        <div className="search-row">
          <label className="employee-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
              placeholder={config.searchPlaceholder}
            />
          </label>
          <label className="hris-inline-select">
            <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
              <option>All statuses</option>
              {statuses.map((value) => <option key={value}>{value}</option>)}
            </select>
          </label>
          <button className="clear-filter" onClick={reset}>Clear</button>
        </div>
      </section>

      <section className="employee-table-panel">
        <div className="directory-heading">
          <div>
            <h2>{config.tableTitle}</h2>
            <p>{filtered.length} record{filtered.length === 1 ? '' : 's'} shown</p>
          </div>
          <span className="sync-note"><Clock3 size={15} /> Mock data for front-end preview</span>
        </div>
        <div className="table-scroll">
          <table className="employee-table hris-master-table hris-dummy-table">
            <thead>
              <tr>{config.columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr>
            </thead>
            <tbody>
              {visible.length ? visible.map((row) => (
                <tr key={row.id}>
                  {config.columns.map((column) => (
                    <td key={`${row.id}-${column.key}`}>
                      <Cell row={row} column={{ ...column, icon: Icon }} />
                    </td>
                  ))}
                </tr>
              )) : (
                <tr><td colSpan={config.columns.length} className="empty-state">Tidak ada data yang sesuai dengan filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>Showing {filtered.length ? ((page - 1) * rowsPerPage) + 1 : 0}-{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length} records</span>
          <div className="pagination-controls">
            <label>Rows per page<select value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}><option value={5}>5</option><option value={7}>7</option><option value={10}>10</option><option value={25}>25</option></select></label>
            <div className="pager">
              <button disabled={page === 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button>
              <b>{page}</b>
              <button disabled={page === totalPages} onClick={() => setPage((value) => value + 1)} aria-label="Next page"><ChevronRight size={18} /></button>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default DummyEmployeeManagementPage;
